import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

const BFS_DEALERSHIP_ID_FLAG = 'BFS_DEALERSHIP_ID';
const BFS_PRODUCT_MAPPINGS_FLAG = 'BFS_PRODUCT_MAPPINGS';

/** Mirrors the tenant API `SessionLoanQuoteResponseDto` returned by the session loan quote endpoint. */
export interface BfsSessionLoanQuoteResponse {
  contractNumber: string;
  transactionType: string;
  result: string;
  message: string;
  activateLoanQuoteUrl: string | null;
}

/** Confirms server-side Registration PDF prefetch completed (bytes stay on the server). */
export interface BfsPrepareContractProofResponse {
  contractNumber: string;
  hash: string;
  contractProofPdfUrlReady: boolean;
  contractProofPdfBase64Ready: boolean;
}

/** Optional financing inputs for the session-driven BFS loan quote. */
export interface BfsSessionLoanQuoteOptions {
  hash?: string;
  termLength?: number;
  downPaymentAmount?: number;
  collectDownPaymentViaBfs?: boolean;
  returnUrl?: string;
  budcoProductId?: string;
  contractProofPdfUrl?: string;
  contractProofStorageItemId?: string;
  contractProofPdfBase64?: string;
}

/**
 * Server-side Budco funding check. Only `activated` means the warranty is active; `pending` and
 * `unavailable` stay retryable, and `failed` means the BFS deal ended without financing.
 */
export type BfsFundingStatus = 'activated' | 'pending' | 'unavailable' | 'failed';

export interface BfsConfigStatus {
  configured: boolean;
  /** True when tenant feature flag BFS_DEALERSHIP_ID is set for checkout. */
  dealershipConfigured?: boolean;
  dealershipId?: string | null;
  budcoProductId?: string | null;
}

interface FeatureFlagsResponse {
  feature_flags?: Record<string, unknown>;
}

interface RawBfsConfig {
  dealershipId?: unknown;
  budcoProductId?: unknown;
}

@Injectable()
export class BfsLoanQuoteService {
  constructor(private http: HttpClient) {}

  private apiPrefix(staffView: boolean): string {
    return staffView ? '/v1/bfs' : '/api/bfs';
  }

  /**
   * Prefetch the Registration/Declaration PDF for a checkout session.
   * Overlaps Puppeteer generation with the user choosing Pay Now / Finance so the loan-quote
   * request can reuse the cached proof and open the BFS iframe sooner.
   */
  async prepareContractProof(
    sessionId: string,
    options: { hash?: string } = {},
    staffView = true
  ): Promise<BfsPrepareContractProofResponse> {
    return await firstValueFrom(
      this.http.post<BfsPrepareContractProofResponse>(
        `${this.apiPrefix(staffView)}/loan-quotes/prepare-contract-proof`,
        {
          sessionId,
          ...options,
        }
      )
    );
  }

  /**
   * Request a BFS loan quote for a checkout session. The server builds the BFS payload from the session
   * (purchaser, vehicle, amounts) and, on success, returns the activation URL to load in the iframe.
   */
  async requestLoanQuoteFromSession(
    sessionId: string,
    options: BfsSessionLoanQuoteOptions = {},
    staffView = true
  ): Promise<BfsSessionLoanQuoteResponse> {
    return await firstValueFrom(
      this.http.post<BfsSessionLoanQuoteResponse>(`${this.apiPrefix(staffView)}/loan-quotes/from-session`, {
        sessionId,
        ...options,
      })
    );
  }

  /** Ask the server to confirm funding with Budco; it activates the pending warranty once Budco does. */
  async verifyFunding(contractNumber: string, staffView = true): Promise<BfsFundingStatus> {
    const response = await firstValueFrom(
      this.http.post<{ status: BfsFundingStatus }>(`${this.apiPrefix(staffView)}/loan-quotes/verify-funding`, {
        contractNumber,
      })
    );
    return response.status;
  }

  async getConfigStatus(): Promise<BfsConfigStatus> {
    const [bfsConfig, featureFlagsResponse, rawBfsConfig] = await Promise.all([
      firstValueFrom(this.http.get<{ configured: boolean; dealershipConfigured?: boolean }>('/api/bfs/config')),
      this.getFeatureFlags(),
      this.getRawBfsConfig(),
    ]);

    const featureFlags = featureFlagsResponse?.feature_flags ?? {};
    const dealershipIdFromFlags = this.readFlagString(featureFlags[BFS_DEALERSHIP_ID_FLAG]);
    const productMappingsFromFlags = this.readFlagString(featureFlags[BFS_PRODUCT_MAPPINGS_FLAG]);

    const dealershipIdFromConfig = this.readFlagString(rawBfsConfig?.dealershipId);
    const productMappingsFromConfig = this.readFlagString(rawBfsConfig?.budcoProductId);
    const dealershipId = dealershipIdFromFlags ?? dealershipIdFromConfig ?? null;

    return {
      configured: Boolean(bfsConfig?.configured),
      // Prefer server resolution (feature flag only); fall back to client-visible flag value.
      dealershipConfigured: Boolean(bfsConfig?.dealershipConfigured ?? dealershipIdFromFlags),
      dealershipId,
      budcoProductId: productMappingsFromFlags ?? productMappingsFromConfig ?? null,
    };
  }

  private async getFeatureFlags(): Promise<FeatureFlagsResponse> {
    try {
      return await firstValueFrom(this.http.get<FeatureFlagsResponse>('/api/feature-flags'));
    } catch {
      return { feature_flags: {} };
    }
  }

  private async getRawBfsConfig(): Promise<RawBfsConfig | null> {
    try {
      const config = await firstValueFrom(this.http.get<RawBfsConfig | null>('/api/vsolv-config/bfs_config'));
      return config ?? null;
    } catch {
      return null;
    }
  }

  private readFlagString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }
}
